import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type Phase = 'inhale' | 'hold-in' | 'exhale' | 'hold-out';

const PHASES: { phase: Phase; label: string; seconds: number }[] = [
  { phase: 'inhale', label: 'Breathe in', seconds: 4 },
  { phase: 'hold-in', label: 'Hold', seconds: 4 },
  { phase: 'exhale', label: 'Breathe out', seconds: 4 },
  { phase: 'hold-out', label: 'Hold', seconds: 4 },
];

const MAX_CYCLES = 3;

export function BoxBreathingDemo() {
  const [running, setRunning] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(4);
  const [cycles, setCycles] = useState(0);
  const [done, setDone] = useState(false);

  const current = PHASES[phaseIndex]!;

  useEffect(() => {
    if (!running || done) return;

    if (secondsLeft <= 0) {
      const nextIndex = (phaseIndex + 1) % PHASES.length;
      const completedCycle = nextIndex === 0;
      if (completedCycle) {
        const nextCycles = cycles + 1;
        if (nextCycles >= MAX_CYCLES) {
          setRunning(false);
          setDone(true);
          return;
        }
        setCycles(nextCycles);
      }
      const nextPhase = PHASES[nextIndex]!;
      setPhaseIndex(nextIndex);
      setSecondsLeft(nextPhase.seconds);
      return;
    }

    const timer = window.setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [running, done, secondsLeft, phaseIndex, cycles]);

  const scale =
    current.phase === 'inhale'
      ? 1.15
      : current.phase === 'hold-in'
        ? 1.15
        : current.phase === 'exhale'
          ? 0.85
          : 0.85;

  function start() {
    setDone(false);
    setCycles(0);
    setPhaseIndex(0);
    setSecondsLeft(PHASES[0]!.seconds);
    setRunning(true);
  }

  function reset() {
    setRunning(false);
    setDone(false);
    setCycles(0);
    setPhaseIndex(0);
    setSecondsLeft(PHASES[0]!.seconds);
  }

  return (
    <div className="box-breathing-demo card p-6 sm:p-8">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-md">
          <p className="landing-section-kicker">Try it now</p>
          <h2 className="landing-section-title">Quick calm — box breathing</h2>
          <p className="mt-3 text-sm leading-relaxed text-gray-600">
            A 4-4-4-4 pattern used to steady anxiety. Three cycles — about one minute. The full app
            includes guided timers and more wellness tools.
          </p>
        </div>
        <div className="box-breathing-stage">
          <div
            className="box-breathing-orb"
            style={{
              transform: `scale(${running || done ? scale : 1})`,
            }}
          />
          <p className="mt-4 text-center text-sm font-bold text-gray-900">
            {done ? 'Well done' : running ? current.label : 'Ready?'}
          </p>
          <p className="text-primary-700 text-center text-3xl font-bold tabular-nums">
            {running ? secondsLeft : done ? '✓' : '4'}
          </p>
          {running && !done ? (
            <p className="text-center text-xs text-gray-500">
              Cycle {cycles + 1} of {MAX_CYCLES}
            </p>
          ) : null}
          <div className="mt-4 flex justify-center gap-2">
            {!running && !done ? (
              <button type="button" className="btn-primary" onClick={start}>
                Start demo
              </button>
            ) : null}
            {running ? (
              <button type="button" className="btn-secondary" onClick={reset}>
                Stop
              </button>
            ) : null}
            {done ? (
              <>
                <button type="button" className="btn-secondary" onClick={reset}>
                  Again
                </button>
                <Link to="/register" className="btn-primary">
                  Get the app
                </Link>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
